import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, Activity, CalendarCheck, Target, AlertTriangle, Trophy, Clock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { displayName } from "@/lib/athleteName";
import UncoveredAthletesAlert from "@/components/UncoveredAthletesAlert";

const ALL = "__all";
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const INS = "Dati insufficienti";
const ATT_LABEL = { low_attendance: "Frequenza bassa", consecutive_absences: "Assenze consecutive", unavailable: "Indisponibile / infortunato", no_lesson: "Senza lezione individuale" };

function Kpi({ label, value, sub, icon: Icon, testid }) {
  const insufficient = value === null || value === undefined;
  return (
    <div className="bg-white border border-zinc-200 p-5 rounded-md" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
        <Icon className="w-4 h-4 text-zinc-400" strokeWidth={1.75} />
      </div>
      <div className={`mt-3 font-mono font-semibold tabular ${insufficient ? "text-base text-zinc-400" : "text-3xl"}`}>{insufficient ? INS : value}</div>
      {sub && !insufficient && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Panel({ title, children, testid, right }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4"><h2 className="font-heading font-semibold text-base md:text-lg">{title}</h2>{right}</div>
      {children}
    </div>
  );
}

const Empty = ({ text = INS }) => <div className="text-sm text-zinc-400 py-6 text-center">{text}</div>;

export default function Statistics() {
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [period, setPeriod] = useState("30");
  const [groupId, setGroupId] = useState(ALL);
  const [weapon, setWeapon] = useState(ALL);

  useEffect(() => { api.get("/groups").then((r) => setGroups((r.data || []).filter((g) => !g.archived))).catch(() => {}); }, []);

  const range = useMemo(() => {
    if (period === "season") return { from: data?.period?.season_start || `${new Date().getFullYear()}-09-01`, to: iso(new Date()) };
    return { from: daysAgo(Number(period)), to: iso(new Date()) };
  }, [period, data?.period?.season_start]);

  useEffect(() => {
    const p = { from: range.from, to: range.to };
    if (groupId !== ALL) p.group_id = groupId;
    if (weapon !== ALL) p.weapon = weapon;
    api.get("/stats/overview", { params: p }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range.from, range.to, groupId, weapon]);

  if (!data) return <div className="text-zinc-500">Caricamento…</div>;
  const s = data.summary;
  const rate = s.attendance_rate;
  const groupName = (id) => groups.find((g) => g.id === id)?.name || "";

  return (
    <div className="space-y-8" data-testid="statistics-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Quadro Sportivo</h1>
          <p className="text-zinc-500 mt-1 text-sm">Come sta funzionando sportivamente il Club{groupId !== ALL ? ` · ${groupName(groupId)}` : ""}{weapon !== ALL ? ` · ${weapon}` : ""} · {data.period.from} → {data.period.to}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="stats-filters">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-10" data-testid="stats-period"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="30">Ultimi 30 giorni</SelectItem><SelectItem value="90">Ultimi 90 giorni</SelectItem><SelectItem value="season">Stagione</SelectItem></SelectContent>
          </Select>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger className="w-44 h-10" data-testid="stats-group"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Tutto il Club</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={weapon} onValueChange={setWeapon}>
            <SelectTrigger className="w-36 h-10" data-testid="stats-weapon"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Tutte le armi</SelectItem><SelectItem value="fioretto">Fioretto</SelectItem><SelectItem value="spada">Spada</SelectItem><SelectItem value="sciabola">Sciabola</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-kpis">
        <Kpi label="Atleti attivi" value={s.active_athletes.value} icon={Users} testid="kpi-active" />
        <Kpi label="Agonisti / Non agon." value={s.activity.reliability === "ok" ? `${s.activity.agonista} / ${s.activity.non_agonista}` : null} sub={s.activity.unclassified ? `${s.activity.unclassified} non classificati` : null} icon={Target} testid="kpi-activity" />
        <Kpi label="Presenza media" value={rate.value !== null ? `${rate.value}%` : null} sub={`${rate.present}/${rate.expected} presenze · ${rate.sample_size} sedute`} icon={Activity} testid="kpi-attendance" />
        <Kpi label="Sedute svolte / progr." value={s.sessions.reliability === "ok" ? `${s.sessions.done} / ${s.sessions.planned}` : null} sub={s.minutes.value ? `${s.minutes.value} min svolti` : null} icon={CalendarCheck} testid="kpi-sessions" />
        <Kpi label="Copertura lezioni" value={s.lesson_coverage.value !== null ? `${s.lesson_coverage.value}%` : null} sub={`${data.lessons.count} lezioni`} icon={Clock} testid="kpi-lessons" />
        <Kpi label="Da attenzionare" value={s.attention.reliability === "ok" ? s.attention.value : null} icon={AlertTriangle} testid="kpi-attention" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Presenza per gruppo" testid="stats-groups">
          {data.groups.length === 0 ? <Empty /> : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-zinc-500"><tr><th className="text-left py-1">Gruppo</th><th className="text-right">Sedute</th><th className="text-right">Presenza</th><th className="text-right">Media pres.</th><th className="text-right">Minuti</th></tr></thead>
              <tbody>{data.groups.map((g) => (
                <tr key={g.group_id} className="border-t border-zinc-100 cursor-pointer hover:bg-zinc-50" onClick={() => setGroupId(g.group_id)} data-testid={`stats-group-row-${g.group_id}`}>
                  <td className="py-2 font-medium">{g.name}</td><td className="text-right font-mono">{g.sessions}</td>
                  <td className="text-right font-mono">{g.attendance_rate !== null ? `${g.attendance_rate}%` : "—"}</td>
                  <td className="text-right font-mono">{g.avg_present ?? "—"}</td><td className="text-right font-mono">{g.minutes}</td>
                </tr>))}</tbody>
            </table>)}
        </Panel>
        <Panel title="Andamento settimanale" testid="stats-trend">
          {data.trend.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.trend}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="week" tick={{ fontSize: 10 }} /><YAxis yAxisId="l" tick={{ fontSize: 10 }} domain={[0, 100]} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} /><Tooltip />
                <Line yAxisId="l" type="monotone" dataKey="attendance_rate" name="Presenza %" stroke="#000" strokeWidth={2} dot={false} />
                <Line yAxisId="r" type="monotone" dataKey="minutes" name="Minuti" stroke="#0a7" strokeWidth={2} dot={false} />
                <Line yAxisId="r" type="monotone" dataKey="sessions" name="Sedute" stroke="#888" strokeWidth={1} dot={false} />
              </LineChart>
            </ResponsiveContainer>)}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="stats-distribution">
        {[["Atleti per gruppo", data.distribution.by_group, "name"], ["Atleti per arma", data.distribution.by_weapon, "weapon"], ["Atleti per categoria", data.distribution.by_category, "category"]].map(([t, rows, key]) => (
          <Panel key={t} title={t}>
            {rows.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={Math.max(120, rows.length * 28)}>
                <BarChart data={rows} layout="vertical" margin={{ left: 8 }}><XAxis type="number" hide /><YAxis type="category" dataKey={key} width={110} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#000" radius={[0, 3, 3, 0]} /></BarChart>
              </ResponsiveContainer>)}
          </Panel>))}
      </div>

      <Panel title="Lezioni individuali" testid="stats-lessons">
        {data.lessons.count === 0 ? <Empty text="Dati insufficienti · nessuna lezione individuale registrata nel periodo" /> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="space-y-1">
              <div><span className="text-zinc-500">Lezioni svolte:</span> <b className="font-mono">{data.lessons.count}</b></div>
              <div><span className="text-zinc-500">Atleti coperti:</span> <b className="font-mono">{data.lessons.athletes_covered}/{data.lessons.athletes_total}</b> ({data.lessons.coverage_pct}%)</div>
              <div><span className="text-zinc-500">Ultima lezione:</span> <b className="font-mono">{data.lessons.last_lesson?.slice(0, 10)}</b></div>
            </div>
            <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Per gruppo</div>{data.lessons.by_group.map((g) => <div key={g.group_id} className="flex justify-between border-t border-zinc-100 py-1"><span>{g.name}</span><span className="font-mono">{g.lessons}</span></div>)}</div>
            <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Per tecnico</div>{data.lessons.by_coach.map((c) => <div key={c.coach_id} className="flex justify-between border-t border-zinc-100 py-1"><span>{c.name}</span><span className="font-mono">{c.lessons}</span></div>)}</div>
            <div className="md:col-span-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Senza lezione nel periodo ({data.lessons.not_covered.length})</div><div className="flex flex-wrap gap-1">{data.lessons.not_covered.slice(0, 40).map((a) => <Link key={a.id} to={`/atleti/${a.id}`} className="text-xs px-2 py-1 border border-zinc-200 rounded hover:bg-zinc-50">{displayName(a)}</Link>)}</div></div>
          </div>)}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Da attenzionare" testid="stats-attention">
          <div className="mb-3"><UncoveredAthletesAlert days={30} groupId={groupId !== ALL ? groupId : ""} compact /></div>
          {s.attention.reliability !== "ok" ? <Empty /> : data.attention.length === 0 ? <Empty text="Nessuna segnalazione nel periodo" /> : (
            <div className="divide-y divide-zinc-100 max-h-80 overflow-y-auto">{data.attention.map((a, i) => (
              <Link key={`${a.id}-${a.type}-${i}`} to={`/atleti/${a.id}`} className="flex items-center justify-between py-2 hover:bg-zinc-50 px-1" data-testid={`attention-${a.type}-${a.id}`}>
                <div><div className="text-sm font-medium">{displayName(a)}</div><div className="text-xs text-zinc-500">{(a.group_names || []).join(", ") || "Senza gruppo"} · {a.detail}</div></div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-800">{ATT_LABEL[a.type] || a.type}</span>
              </Link>))}</div>)}
        </Panel>
        <Panel title="Prossime gare (60 giorni)" testid="stats-competitions" right={<Link to="/gare" className="text-xs underline text-zinc-500">Calendario</Link>}>
          {data.competitions.length === 0 ? <Empty text="Nessuna gara nei prossimi 60 giorni" /> : (
            <div className="divide-y divide-zinc-100">{data.competitions.map((c) => (
              <Link key={c.id} to={`/gare/${c.id}`} className="flex items-center justify-between py-2 hover:bg-zinc-50 px-1">
                <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-zinc-500">{c.date} · {c.location || "—"}{c.weapon ? ` · ${c.weapon}` : ""}{c.category ? ` · ${c.category}` : ""}</div></div>
                <div className="text-right text-xs">{c.technical_priority && <span className="font-mono font-bold mr-2">{c.technical_priority}</span>}{c.planning_status && <span className="text-zinc-500">{c.planning_status}</span>}<div className="text-zinc-500">{c.invited > 0 ? `${c.invited} convocati` : "convocazioni non inserite"}</div></div>
              </Link>))}</div>)}
        </Panel>
      </div>

      <div className="border border-dashed border-zinc-300 rounded-md p-4 text-xs text-zinc-500" data-testid="stats-data-quality">
        <span className="uppercase tracking-wider font-semibold mr-3">Qualità dati (bonifica anagrafica)</span>
        <span className="mr-4">Senza gruppo: <b className="font-mono">{data.data_quality.no_group}</b></span>
        <span className="mr-4">Senza arma: <b className="font-mono">{data.data_quality.no_weapon}</b></span>
        <span className="mr-4">Sesso mancante: <b className="font-mono">{data.data_quality.no_gender}</b></span>
        <span>Attività non classificata: <b className="font-mono">{data.data_quality.unclassified_activity}</b></span>
        <Trophy className="hidden" />
      </div>
    </div>
  );
}
